import {
  streamLainAnimationController,
  streamLainOverlayKeyframes,
} from '@blocksuite/affine-shared/utils';
import { css, html, LitElement } from 'lit';

export class DatabaseProjectionStatusOverlay extends LitElement {
  static override properties = {
    label: { type: String },
    visible: { type: Boolean },
  };

  static override styles = css`
    :host {
      bottom: 50px;
      display: block;
      opacity: 0;
      pointer-events: none;
      position: absolute;
      right: 16px;
      transform: translateY(6px) scale(0.96);
      z-index: 5;
    }

    .status {
      align-items: center;
      background: var(--streamlain-v2-layer-background-primary);
      border: 1px solid var(--streamlain-v2-layer-insideBorder-border);
      border-radius: 999px;
      box-shadow: 0 8px 24px rgb(0 0 0 / 14%);
      color: var(--streamlain-v2-text-secondary);
      display: inline-flex;
      font-size: 12px;
      font-weight: 500;
      line-height: 18px;
      min-height: 30px;
      padding: 5px 10px;
      white-space: nowrap;
    }
  `;

  label = '';

  visible = false;

  private hasSettledInitialState = false;

  override disconnectedCallback(): void {
    streamLainAnimationController.cancel(this);
    super.disconnectedCallback();
  }

  protected override updated(changedProperties: Map<PropertyKey, unknown>) {
    super.updated(changedProperties);
    if (!changedProperties.has('visible')) return;

    if (!this.hasSettledInitialState) {
      this.hasSettledInitialState = true;
      if (!this.visible) {
        this.settledStyle(false);
        return;
      }
    }

    const visible = this.visible;
    const phase = visible ? 'enter' : 'exit';
    const keyframes = streamLainOverlayKeyframes('bottom', phase);
    const handle = streamLainAnimationController.play(
      this,
      [keyframes.from, keyframes.to],
      phase
    );
    void handle.finished.then(outcome => {
      if (outcome === 'finished' && this.visible === visible) {
        this.settledStyle(visible);
      }
    });
  }

  private settledStyle(visible: boolean): void {
    const frame = streamLainOverlayKeyframes(
      'bottom',
      visible ? 'enter' : 'exit'
    );
    const target = frame.to;
    this.style.opacity = String(target.opacity);
    this.style.transform = String(target.transform);
  }

  protected override render() {
    return html`<div
      class="status"
      data-testid="database-projection-status-overlay"
      role="status"
      aria-hidden=${this.visible ? 'false' : 'true'}
      aria-live="polite"
    >
      ${this.label}
    </div>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'database-projection-status-overlay': DatabaseProjectionStatusOverlay;
  }
}
